import { memo, useCallback, useRef } from 'react';

import { type AirActionTrackingLocation, useTrackDuplicatedCanvas } from '@air/analytics';
import { ConfirmationModal } from '@air/component-confirmation-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useDuplicateCanvasMutation } from '~/components/Modals/DuplicateCanvas/hooks/useDuplicateCanvasMutation';
import { useSelectedCanvasId } from '~/hooks/canvases/useSelectedCanvasId';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type CanvasListItem } from '~/swr-hooks/canvases/types';

export type DuplicateCanvasModalProps = {
  canvas: CanvasListItem;
  trackLocation: AirActionTrackingLocation;
  /** Opening board. Passed through to selectCanvas; nested URLs apply only when ui2026. */
  boardId?: string;
};

export const DuplicateCanvasModal = memo(
  ({ onClose, canvas, trackLocation, boardId }: AirModalProps<DuplicateCanvasModalProps>) => {
    const { showToast } = useToasts();
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
    const { currentWorkspace } = useCurrentWorkspace();
    const { mutateAsync: duplicateCanvasMutation, isPending: isDuplicating } = useDuplicateCanvasMutation();
    const { selectCanvas } = useSelectedCanvasId();
    const { trackDuplicatedCanvas } = useTrackDuplicatedCanvas();
    const isDismissedRef = useRef<boolean>();

    const handleDismiss = useCallback(() => {
      isDismissedRef.current = true;
      onClose();
    }, [onClose]);

    const duplicate = useCallback(async () => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspaceId found');
      }

      try {
        const duplicateCanvas = await duplicateCanvasMutation({ workspaceId, canvasId: canvas.id });

        // The mutation can resolve after the user has already dismissed the modal, so its
        // success/error side effects (navigation, toasts) should be skipped at that point.
        if (isDismissedRef.current) {
          return;
        }

        selectCanvas(duplicateCanvas.id, {
          ...(boardId ? { boardInfo: { boardId, boardTitle: canvas.boardTitle ?? undefined } } : {}),
        });
        trackDuplicatedCanvas({
          canvasId: duplicateCanvas.id,
          sourceCanvasId: canvas.id,
          location: trackLocation,
          boardId: duplicateCanvas.boardId ?? undefined,
        });

        showToast('Canvas duplicated');
        onClose();
      } catch (error) {
        reportErrorToBugsnag({
          error,
          context: 'Failed to duplicate canvas',
          metadata: {
            canvasId: canvas.id,
            trackLocation,
          },
        });

        if (!isDismissedRef.current) {
          showToast('There was an error while trying to duplicate this Canvas. Please try again later.');
        }
      }
    }, [
      boardId,
      canvas.boardTitle,
      canvas.id,
      currentWorkspace?.id,
      duplicateCanvasMutation,
      onClose,
      selectCanvas,
      showToast,
      trackDuplicatedCanvas,
      trackLocation,
    ]);

    if (showingSubscriptionExpiredModal) {
      return null;
    }

    return (
      <ConfirmationModal
        id="duplicate-canvas-modal"
        isOpen
        title="Duplicate Canvas"
        ctaPrimaryText="Duplicate"
        isLoading={isDuplicating}
        isDisabled={isDuplicating}
        onConfirm={duplicate}
        data-testid="DUPLICATE_CANVAS_MODAL"
        description="Duplicating a Canvas will make an exact copy of the Canvas, including its layout and content."
        onDismiss={handleDismiss}
      />
    );
  },
);

DuplicateCanvasModal.displayName = 'DuplicateCanvasModal';
